import { Breadcrumbs, MainContentLayout, showToast, ToastTypeEnum } from '@wonderschool/common-base-ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../Components/Shared/PageHeader';
import { fetchOrganizationRooms } from '../../api/firebase/rooms';
import { usePrimaryLocation } from '../../hooks/useLocations';
import { useOrganization } from '../../hooks/useOrganizations';
import { useRooms } from '../../hooks/useRooms';
import { useUser } from '../../hooks/useUser';
import { paths } from '../../navigation/paths';
import { logError } from '../../rollbar';
import { confirmAddToBilling } from '../AddToBilling/addToBillingAPI';
import { Step2Roster } from './Step2Roster';
import { Step3Review } from './Step3Review';
import { StepBilling } from './StepBilling';
// Done step removed — on confirm we go to the student list with a success toast.
import { StepSwitchGuide } from './StepSwitchGuide';
import { WizardSteps } from './WizardSteps';
import { confirmFamilies } from './addFamiliesAPI';
import { matchRosterAgainstExisting, RowMatch } from './matchExisting';
import { blankRow, childFullName, normalizeName, RosterRow, Source, toBillableRows, validateRow } from './types';

/**
 * Add Families, 4-step bulk-onboarding wizard.
 *
 * Replaces the previous stub that redirected to Nova. Writes directly
 * to Firestore via the existing `saveStudent` + `saveContact` APIs the
 * single-student "Add Student" page uses, so families created here are
 * indistinguishable from ones added elsewhere in CCMS.
 *
 * Routes: `/ccms/add-families` and `/add-families` (alias).
 */
/**
 * The wizard itself, with no page chrome. Kept separate from the routed
 * page so it can be embedded elsewhere later (e.g. a focused full-page
 * takeover) by rendering <AddFamiliesFlow chrome={false} />.
 */
export function AddFamiliesFlow({ chrome = true }: { chrome?: boolean } = {}) {
  const { t } = useTranslation();
  const organization = useOrganization() as any;
  const location = usePrimaryLocation();
  const roomsFromStore = useRooms() as any[];
  const user = useUser() as any;
  const navigate = useNavigate();

  // Rooms normally arrive via the app-wide Redux listener. On some entry paths
  // (a deep link straight into the wizard, or a room-scoped session whose
  // per-user room query returns nothing) that slice is still empty when the
  // wizard mounts, which left the Room dropdown showing "No options found" and
  // blocked Continue with no way to pick a room. Fall back to a direct fetch of
  // the org's rooms (the same API the rest of CCMS uses, and unscoped so a
  // director sees every room) so the wizard always has rooms and can default
  // to one — every school has at least its primary room.
  const [fallbackRooms, setFallbackRooms] = useState<any[] | null>(null);
  useEffect(() => {
    if ((roomsFromStore?.length ?? 0) > 0) return;
    const orgId = organization?.id;
    if (!orgId) return;
    let cancelled = false;
    fetchOrganizationRooms(orgId)
      .then((res) => {
        if (!cancelled) setFallbackRooms(res?.list ?? []);
      })
      .catch((err) => logError('AddFamilies: rooms fallback fetch failed', err));
    return () => {
      cancelled = true;
    };
  }, [roomsFromStore, organization?.id]);

  const rooms = useMemo(
    () => (roomsFromStore?.length ? roomsFromStore : fallbackRooms) ?? [],
    [roomsFromStore, fallbackRooms]
  );

  // Students must be saved into a real room or the students list, which is
  // room-scoped (rooms array-contains-any the director's rooms), so a student
  // must be saved into one of the org's real rooms or the list hides them. The
  // Room dropdown (Step 2) and CSV room-matching bind to these. Sorted by name
  // so "the first active room" (the default new rows fall back to) is stable —
  // this is the school's primary/default room.
  const activeRooms = useMemo(
    () =>
      (rooms || [])
        .filter((r) => !r?.isArchived)
        .map((r) => ({ id: r.id, name: r.name }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [rooms]
  );
  const defaultRoomId = activeRooms[0]?.id ?? '';

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [roster, setRoster] = useState<RosterRow[]>(() => [blankRow(), blankRow(), blankRow()]);
  const [frequency, setFrequency] = useState<RosterRow['billingFrequency']>('monthly');
  const [importedFromFileName, setImportedFromFileName] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  // The flow opens on the source picker ("Where is your student list now?").
  // `source` records which path the provider took; `showSwitchGuide` is true
  // while the picker is on screen.
  const [source, setSource] = useState<Source>('switch');
  const [showSwitchGuide, setShowSwitchGuide] = useState(true);
  // Which tool/spreadsheet the provider picked, so the upload step can show
  // that source's export instructions.
  const [selectedSourceId, setSelectedSourceId] = useState<string | undefined>();
  const isRosterFlow = source === 'switch' || source === 'manual';

  useEffect(() => {
    document.title = t('addFamilies.results.documentTitle');
  }, [t]);

  // Rooms load async from Redux, so the initial rows can be created before
  // defaultRoomId is known. Once it resolves, backfill any row still missing a
  // room so every student is saved into a real, list-visible room.
  useEffect(() => {
    if (!defaultRoomId) return;
    setRoster((rows) =>
      rows.some((r) => !r.room) ? rows.map((r) => (r.room ? r : { ...r, room: defaultRoomId })) : rows
    );
  }, [defaultRoomId]);

  function onImport(rows: RosterRow[], fileName?: string) {
    setRoster([...rows, blankRow(defaultRoomId)]);
    setImportedFromFileName(fileName);
    setStep(2);
  }

  async function onConfirm() {
    if (!organization?.id) {
      logError('AddFamilies: no organization id, cannot save');
      return;
    }

    // Duplicate rows inside one import (the same child pasted twice) would
    // each classify as "new" and create two students AND two plans — matching
    // runs before any writes, so it can't see the first copy. Keep only the
    // first row per (parent email + child name).
    const seenRowKeys = new Set<string>();
    const validRows = roster
      .filter((r) => !validateRow(r).isBlank && validateRow(r).errors.length === 0)
      .filter((r) => {
        const key = `${r.parentEmail.trim().toLowerCase()}|${normalizeName(childFullName(r))}`;
        if (seenRowKeys.has(key)) return false;
        seenRowKeys.add(key);
        return true;
      });

    setIsSaving(true);
    try {
      // Check each row against families already in the system BEFORE writing
      // anything: an existing child is never re-created or overwritten, and a
      // re-imported roster can't double-bill anyone. Matching decides
      // FINANCIAL writes, so a failed lookup aborts the confirm — proceeding
      // on missing evidence would re-create and re-bill existing families.
      let matches: RowMatch[];
      try {
        matches = await matchRosterAgainstExisting(organization.id, validRows);
      } catch (err) {
        logError('AddFamilies: existing-family match failed, aborting confirm', err);
        showToast(ToastTypeEnum.Error, t('addFamilies.results.duplicateCheckError'));
        return;
      }

      const result = await confirmFamilies({
        organizationId: organization.id,
        locationId: location?.id,
        rows: validRows,
        addToBilling: false,
        defaultRoomId,
        matches,
        // Send the parent invite now (the standard account-setup email). These are
        // existing, enrolled families, so there's no reason to defer their invite.
        sendEmailsLater: false,
      });

      // Put every billable family on a real recurring invoice plan, through the
      // same path the Add-to-billing page uses (CCMS-8066: the wizard used to
      // stamp billing metadata on the student doc and stop, so the Billing Plans
      // table stayed empty). `toBillableRows` owns the due-day → first-due-date
      // mapping for both pages; the gate here is `billableStudentsByRowId`, which
      // covers newly created students AND existing ones that aren't billed yet.
      const billedRows = toBillableRows(validRows, (r) => result.billableStudentsByRowId.has(r.id));
      let plansCreated = 0;
      if (billedRows.length > 0) {
        try {
          plansCreated = await confirmAddToBilling({
            organizationId: organization.id,
            rows: billedRows,
            studentsById: result.billableStudentsByRowId,
            createdBy: { id: user?.uid, displayName: user?.displayName },
            // Plan dates are interpreted in the school's timezone downstream;
            // 'utc' shifts send/due anchors a day for US schools.
            timezone: location?.timezone || 'utc',
          });
        } catch (err) {
          // Families are already saved; a billing failure must not report the
          // whole confirm as failed. Surface it separately below.
          logError('AddFamilies: creating recurring plans failed', err);
        }
      }

      // Land the user on the student list (where the new students now appear)
      // and confirm with a toast rather than a standalone "done" page. Only
      // claim billing when plans were actually created (never show success UI
      // for an operation that wrote nothing).
      const n = result.studentsCreated;
      // Per-row failures must be visible — a batch where nothing saved must
      // not look like a success.
      if (result.errors.length > 0) {
        showToast(ToastTypeEnum.Error, t('addFamilies.results.addFailed', { count: result.errors.length }));
      }
      if (n > 0) {
        showToast(
          ToastTypeEnum.Success,
          plansCreated > 0 && plansCreated === billedRows.length
            ? t('addFamilies.results.addedWithBilling', { count: n })
            : t('addFamilies.results.addedToRoster', { count: n })
        );
      } else if (plansCreated > 0) {
        // Nothing new to add, but existing families were put on billing —
        // "0 students added" would read as a no-op and hide the real change.
        showToast(ToastTypeEnum.Success, t('addFamilies.results.existingAddedToBilling', { count: plansCreated }));
      }
      if (plansCreated < billedRows.length) {
        showToast(
          ToastTypeEnum.Warning,
          t('addFamilies.results.billingPlansFailed', {
            failed: billedRows.length - plansCreated,
            total: billedRows.length,
          })
        );
      }
      // Rows matched to families already in the system were deliberately not
      // re-added — say so, or "I imported 10 and only 7 appeared" reads as a bug.
      if (result.existingMatched > 0) {
        showToast(ToastTypeEnum.Info, t('addFamilies.results.existingUnchanged', { count: result.existingMatched }));
      }
      navigate(paths.students);
    } catch (err) {
      logError('AddFamilies confirm failed', err);
      showToast(ToastTypeEnum.Error, t('addFamilies.results.unexpectedError'));
    } finally {
      setIsSaving(false);
    }
  }

  const content = (
    <div className="max-w-6xl space-y-4">
      <Breadcrumbs
        items={[
          { label: t('addFamilies.results.studentsBreadcrumb'), callback: () => navigate(paths.students) },
          { label: t('addFamilies.page.title'), isCurrentLevel: true },
        ]}
      />

      {/* Keyed by screen so each step fades/rises in instead of hard-swapping. */}
      <div key={showSwitchGuide ? 'guide' : step} className="motion-safe:animate-stepIn">
        {showSwitchGuide && (
          <StepSwitchGuide
            onContinue={(id) => {
              // Import source → the roster page shows the upload zone in place,
              // then fills itself in. No separate upload step/navigation.
              setSelectedSourceId(id);
              setSource('switch');
              setImportedFromFileName(undefined);
              setRoster([blankRow(defaultRoomId), blankRow(defaultRoomId), blankRow(defaultRoomId)]);
              setShowSwitchGuide(false);
              setStep(2);
            }}
            onEnterByHand={() => {
              setSource('manual');
              setSelectedSourceId(undefined);
              setImportedFromFileName(undefined);
              setShowSwitchGuide(false);
              setRoster([blankRow(defaultRoomId), blankRow(defaultRoomId), blankRow(defaultRoomId)]);
              setStep(2);
            }}
          />
        )}

        {isRosterFlow && !showSwitchGuide && step === 2 && (
          <Step2Roster
            stepper={<WizardSteps current={step - 2} />}
            roster={roster}
            sourceId={selectedSourceId}
            importedFromFileName={importedFromFileName}
            rooms={activeRooms}
            defaultRoomId={defaultRoomId}
            onChange={setRoster}
            onImport={onImport}
            onBack={() => setShowSwitchGuide(true)}
            onContinue={() => setStep(3)}
          />
        )}

        {isRosterFlow && !showSwitchGuide && step === 3 && (
          <StepBilling
            stepper={<WizardSteps current={step - 2} />}
            roster={roster}
            sourceId={selectedSourceId}
            frequency={frequency}
            rooms={activeRooms}
            onChange={setRoster}
            onFrequencyChange={(freq) => {
              setFrequency(freq);
              setRoster((rows) => rows.map((r) => ({ ...r, billingFrequency: freq })));
            }}
            onBack={() => setStep(2)}
            onContinue={() => setStep(4)}
          />
        )}

        {isRosterFlow && !showSwitchGuide && step === 4 && (
          <Step3Review
            stepper={<WizardSteps current={step - 2} />}
            roster={roster}
            organizationName={organization?.name}
            isSaving={isSaving}
            onBack={() => setStep(3)}
            onConfirm={onConfirm}
          />
        )}
      </div>
    </div>
  );

  if (!chrome) return content;

  return (
    <MainContentLayout>
      <PageHeader pageName={t('addFamilies.page.title')} classes="add-families-page" />
      {content}
    </MainContentLayout>
  );
}

/** Routed page: the flow wrapped in the CCMS page template. */
export function AddFamilies() {
  return <AddFamiliesFlow />;
}

export default AddFamilies;
